import dayjs, { type Dayjs } from "@punchup/utils/dayjs";
import type { Database, Json } from "../../supabase.types";

/**
 * Wire types for the DB-backed newsletter system (tables:
 * `public.newsletter_series`, `public.newsletter_issues`).
 *
 * `newsletter_issues` is the single lifecycle table for an issue, from first
 * draft keystroke through a completed send — there is no separate drafts
 * table (see `supabase/migrations/20260719000001_create_newsletter_issues.sql`
 * for the full state-machine rationale). Row/Insert/Update aliases are
 * extracted straight from the generated `Database[...]` shape (hand-added in
 * the same commit as the migration; `npm run gen_types` post-apply is
 * expected to diff to nothing) — never hand-write these shapes.
 */
export type NewsletterSeriesRow =
  Database["public"]["Tables"]["newsletter_series"]["Row"];

export type NewsletterIssueRow =
  Database["public"]["Tables"]["newsletter_issues"]["Row"];

export type NewsletterIssueStatus =
  Database["public"]["Enums"]["newsletter_issue_status"];

/**
 * `newsletter_series.time_of_day` is a plain `text` column (checked, not a
 * DB enum — see the migration header) so the vocabulary can grow without a
 * migration. This literal union is the app-side narrowing of that column;
 * `isNewsletterTimeOfDay` below is the runtime guard a fetcher must run
 * before trusting a DB-read string as this type.
 */
export type NewsletterTimeOfDay = "morning" | "afternoon" | "evening";

export function isNewsletterTimeOfDay(
  value: string
): value is NewsletterTimeOfDay {
  return value === "morning" || value === "afternoon" || value === "evening";
}

/**
 * Gate Q3 (2026-07-17 review gate): morning 9:00 / afternoon 13:00 / evening
 * 18:00, always America/New_York regardless of the recipient's or server's
 * own timezone — the same fixed civic-time convention `create-job`/
 * `validation.ts` use for `scheduledForDatetimeEST`.
 */
export const TIME_OF_DAY_SEND_HOURS_ET: Record<NewsletterTimeOfDay, number> = {
  morning: 9,
  afternoon: 13,
  evening: 18,
};

/**
 * Resolves an issue's send date + a series' `time_of_day` to a concrete
 * `send_at` instant: the wall-clock hour in America/New_York on `dateStr`,
 * converted to UTC. Pure — no DB/network access — so it's covered directly
 * by unit tests including DST-boundary dates (`TZ=UTC` makes the assertions
 * deterministic regardless of the runner's local zone).
 *
 * `dateStr` must be `YYYY-MM-DD` (the `date`-typed `scheduled_for`/
 * `planned_for` columns' wire format). Cadence math itself never runs here
 * or in SQL — this only converts an already-decided calendar date + time-of-
 * day slot into the timestamptz the cron scans.
 */
export function resolveSendAt(
  dateStr: string,
  timeOfDay: NewsletterTimeOfDay
): Dayjs {
  const hour = TIME_OF_DAY_SEND_HOURS_ET[timeOfDay];
  return resolveSendAtExact(dateStr, `${String(hour).padStart(2, "0")}:00`);
}

/** The wall-clock zone every newsletter send time is entered and shown in. */
export const NEWSLETTER_SEND_TZ = "America/New_York";

/** `resolveSendAt` for an exact `HH:MM` wall-clock time in `NEWSLETTER_SEND_TZ`. */
export function resolveSendAtExact(dateStr: string, timeStr: string): Dayjs {
  const wallClock = `${dateStr} ${timeStr}:00`;
  return dayjs.tz(wallClock, NEWSLETTER_SEND_TZ).utc();
}

/**
 * Sentinel `southpaw_jobs.email_template` value stamped by the newsletter
 * dispatch helper (`apps/web/lib/newsletter/server/dispatch-newsletter-issue.ts`)
 * to mark a job whose `email_body` is ALREADY a fully-rendered HTML document
 * (produced at dispatch time from the issue's `doc`), as opposed to the raw
 * comedian-authored body text every other southpaw job stores (wrapped by
 * `genSouthpawEmailTemplate`/`genVenueSouthpawEmailTemplate` at execute-job
 * time). Read by:
 *  - `execute-job/route.ts`'s sentinel branch (skip the generic template wrap)
 *  - job-history render surfaces (`EmailJobListRow`/`EmailJobCard`/
 *    `EmailJobDetailOverlay`) that must strip tags instead of printing markup
 *  - `isNewsletterJob` below (the Email & SMS jobs tables hide these rows)
 * Exported here (not from the server-only dispatch helper) so client
 * components can read it without crossing the client/server boundary.
 */
export const NEWSLETTER_JOB_EMAIL_TEMPLATE = "editor_doc_v1" as const;

/** Stands in for the southpaw job id in a pre-rendered newsletter body; execute-job swaps in the real id. */
export const NEWSLETTER_JOB_ID_PLACEHOLDER = "__SOUTHPAW_JOB_ID__" as const;

/** True for a southpaw job minted by a newsletter dispatch. */
export function isNewsletterJob(job: { email_template: string }): boolean {
  return job.email_template === NEWSLETTER_JOB_EMAIL_TEMPLATE;
}

/** `southpaw_jobs.campaign_name` stamped on every newsletter-originated job —
 *  the join key per-series/per-send analytics use against
 *  `mailgun_southpaw_aggregated_metrics` (no new columns needed on
 *  `southpaw_jobs` itself; see the migration header). */
export const NEWSLETTER_CAMPAIGN_NAME = "newsletter" as const;

// ---------------------------------------------------------------------------
// API request/response DTOs for the future v2 newsletter routes (PR10+).
// Not yet consumed by any route in this PR — the server fetchers below
// return/accept these shapes so the route layer is a thin pass-through when
// it lands. camelCase, `Request`/`Response` suffix — mirrors
// email-template.types.ts.
// ---------------------------------------------------------------------------

export interface ListNewsletterSeriesResponse {
  series: NewsletterSeriesRow[];
}

/** Create-or-update payload. Omit `id` to create; include it to update an
 *  existing series. `cadence`/`ends` are opaque `Json` here — the
 *  discriminated-union shapes stay a client concern (`cadence.ts`'s
 *  `sanitizeCadence`), validated before this DTO is built. */
export interface UpsertNewsletterSeriesRequest {
  id?: string;
  comedianId: string;
  name: string;
  cadence: Json;
  enabled?: boolean;
  sendMode?: "auto" | "manual";
  timeOfDay?: NewsletterTimeOfDay;
  reminderLeadTime?: string;
  ends?: Json;
  editionNoun?: string | null;
  colorIndex?: number | null;
  lastNudgeDismissedAt?: string | null;
  /** Optimistic-concurrency token; omitted on create. */
  expectedRevision?: number;
}

export interface UpsertNewsletterSeriesResponse {
  series: NewsletterSeriesRow;
}

export interface ListNewsletterIssuesResponse {
  issues: NewsletterIssueRow[];
}

export interface GetNewsletterIssueResponse {
  issue: NewsletterIssueRow;
}

/** Autosave payload for a `draft`/`scheduled` issue. Omit `id` to create. */
export interface UpsertNewsletterIssueDraftRequest {
  id?: string;
  comedianId: string;
  seriesId?: string | null;
  subject?: string;
  doc?: Json;
  previewText?: string;
  plannedFor?: string | null;
  expectedRevision?: number;
}

export interface UpsertNewsletterIssueDraftResponse {
  issue: NewsletterIssueRow;
}

/** `schedule` stamps `approved_at`/`approved_by` when unset — the row may come back approved. */
export interface ScheduleNewsletterIssueResponse {
  issue: NewsletterIssueRow;
}

export interface ApproveNewsletterIssueResponse {
  issue: NewsletterIssueRow;
}

export interface SendNewsletterIssueNowResponse {
  issue: NewsletterIssueRow;
  southpawJobId: string;
  audienceCount: number;
}

export interface PatchNewsletterIssueResponse {
  issue: NewsletterIssueRow;
}

// The `unschedule` PATCH action reverts a row to `status: 'draft'` with
// `send_at`/`scheduled_for`/`approved_at`/`approved_by`/`dispatch_error`
// cleared. It backs the compose Remove-schedule action and the
// `failed`-row revival step (a user action on a `failed` issue reverts it
// to `draft` first so the draft|scheduled-only transition guards accept
// it — `failed` is user-retryable). The route composes the update inline
// (no dedicated fetcher — unlike `unapprove`, which calls
// `unapproveNewsletterIssue`).
export const NEWSLETTER_ISSUE_REVIVABLE_STATUS = "failed" as const;

export const NEWSLETTER_SUBJECT_REQUIRED_MESSAGE = "Subject is required";

export function isNewsletterSubjectMissing(
  subject: string | null | undefined
): boolean {
  return !subject?.trim();
}
